import { Icons, SPACING_6, media } from '@mnd-frontend/ui';
import { type Dispatch, type SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import mndUrl, { SubDomain } from '../../../../../lib/mndUrl';
import routes from '../../../../../routes';
import { type Item, type MainMenuItem, MenuState, type Pressroom } from '../types';
import { getMatchingRoutes } from '../utils';
import LogoOrPressroomSelector from './LogoOrPressroomSelector';
import NavigationItem from './NavigationItem';
import TrialMenuItem from './TrialMenuItem';

const Nav = styled.nav`
  background-color: transparent;
  position: fixed;
  z-index: 1000;
  top: 0;
  bottom: 0;
  width: var(--mnd-navigation-width);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  height: 100%;
  padding: 1.5rem 0;

  ${media.compactDesktopNavigation} {
    padding-bottom: 1rem;
  }
  > ul:last-child {
    margin-bottom: 1rem;
    ${media.compactDesktopNavigation} {
      margin-bottom: 0;
    }
  }
`;

const NavList = styled.ul<{ $trial?: boolean }>`
  list-style-type: none;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  margin-top: ${({ $trial }) => ($trial ? SPACING_6 : '0')};
  &#top-group {
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

interface DesktopNavigationProps {
  loading: boolean;
  navigationItems: MainMenuItem[];
  newNavigationInformationBannerShown: boolean;
  renderPressroomSelector: boolean;
  isSubNavOpen: MenuState | null;
  trial: boolean | undefined;
  pressrooms: Pressroom[];
  topGroup: MenuState[];
  isOnlyJournalist: boolean;
  journalistItems: Item[];
  newsroomLogo?: string | null;
  pressroomName: string;
  siteCode?: string;
  setIsSubNavOpen: Dispatch<SetStateAction<MenuState | null>>;
}

const DesktopNavigation = ({
  loading,
  navigationItems,
  newNavigationInformationBannerShown,
  renderPressroomSelector,
  isSubNavOpen,
  trial,
  pressrooms,
  topGroup,
  isOnlyJournalist,
  journalistItems,
  newsroomLogo,
  pressroomName,
  siteCode,
  setIsSubNavOpen,
}: DesktopNavigationProps) => {
  const { t } = useTranslation();
  const [showNewNavigationInformationBanner, setShowNewNavigationInformationBanner] = useState(
    !newNavigationInformationBannerShown,
  );
  const navListRef = useRef<HTMLDivElement>(null);
  const topGroupItems = navigationItems.filter(item => topGroup.includes(item.menuState));
  const bottomGroupItems = navigationItems.filter(item => !topGroup.includes(item.menuState));
  return (
    <Nav aria-label={t('aria_label_main_navigation')} ref={navListRef}>
      {trial && (
        <TrialMenuItem
          isOpen={isSubNavOpen === MenuState.Trial}
          open={() => setIsSubNavOpen(MenuState.Trial)}
          close={() => setIsSubNavOpen(state => (state === MenuState.Trial ? null : state))}
          positionAbsolute={true}
        />
      )}
      <NavList id="top-group" $trial={trial}>
        <LogoOrPressroomSelector
          pressroomName={pressroomName}
          loading={loading}
          newsroomLogo={newsroomLogo}
          pressroomSelector={renderPressroomSelector}
          siteCode={siteCode}
          itemData={{
            heading: t('menu_pressroom_heading'),
            menuState: MenuState.PressroomSelector,
            newsroomLogo,
            items:
              pressrooms.map(p => {
                return {
                  text: p.name,
                  url: mndUrl(SubDomain.Publish, routes.dashboard({ pressroomId: p.id })),
                  siteCode: p.siteCode,
                };
              }) ?? [],
          }}
          isSubNavOpen={isSubNavOpen}
          open={() => setIsSubNavOpen(MenuState.PressroomSelector)}
          close={() =>
            setIsSubNavOpen(state => (state === MenuState.PressroomSelector ? null : state))
          }
        />

        {topGroupItems.map(navItem => (
          <NavigationItem
            key={navItem.menuState}
            topGroup={true}
            itemData={{
              ...navItem,
              matchingRoutes: getMatchingRoutes(navItem.items),
            }}
            isSubNavOpen={isSubNavOpen}
            open={() => setIsSubNavOpen(navItem.menuState)}
            close={() => setIsSubNavOpen(state => (state === navItem.menuState ? null : state))}
          />
        ))}
        {isOnlyJournalist && (
          <NavigationItem
            topGroup={false}
            itemData={{
              heading: 'Journalist',
              menuState: MenuState.User,
              icon: <Icons.User />,
              items: journalistItems,
            }}
            isSubNavOpen={isSubNavOpen}
            open={() => setIsSubNavOpen(MenuState.User)}
            close={() => setIsSubNavOpen(state => (state === MenuState.User ? null : state))}
          />
        )}
      </NavList>
      <NavList id="bottom-group">
        {bottomGroupItems.map(navItem => (
          <NavigationItem
            key={navItem.menuState}
            itemData={{
              ...navItem,
              matchingRoutes: getMatchingRoutes(navItem.items),
            }}
            isSubNavOpen={isSubNavOpen}
            open={() => setIsSubNavOpen(navItem.menuState)}
            close={() => setIsSubNavOpen(state => (state === navItem.menuState ? null : state))}
          />
        ))}
      </NavList>
    </Nav>
  );
};

export default DesktopNavigation;
